import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { fieldKindFor } from "@storyboard/dispatch-view/fieldKinds";
import { compileDispatchFilter, type DispatchControlValues, hasTimeOperator } from "@storyboard/dispatch-view/query";
import type {
  DispatchViewStatePatch,
  ResolvedDispatchQuery,
  ResolvedDispatchView,
} from "@storyboard/dispatch-view/resolve";
import type {
  DispatchControl,
  DispatchControlValue,
  DispatchField,
  DispatchSort,
  DispatchViewDefinition,
} from "@storyboard/dispatch-view/view";
import { NONE } from "@storyboard/dispatch-view/view";
import type { FilterNode, ProjectedRecord } from "@storyboard/driver-query/predicate";
import { matchesFilter } from "@storyboard/driver-query/predicate";
import { labelForColumnId } from "@/components/Dispatch/columns";
import { columnIdForConditionField } from "@/components/Dispatch/DispatchColumnMenu";
import type { DisplaySort } from "@/components/Dispatch/DispatchDisplayMenu";
import type { QueryChip } from "@/components/Dispatch/DispatchQueryStrip";
import { ampli } from "@/config";
import type { DriverRow, TagDefinition } from "@/types/driver";
import {
  type ConditionNaming,
  chosenValues,
  conditionField,
  replaceFieldConditions,
  valuesCondition,
} from "./adhocConditions";
import { foldShelfGroups, withAssignmentShelf } from "./assignmentShelf";
import type { AssignmentFold } from "./assignments";
import type { DispatchItem } from "./dispatchItems";
import type { DispatchTableRow } from "./dispatchTableRow";
import { type DispatchGroup, groupDispatchRows, toggleCollapsedGroup } from "./groupDispatchRows";
import { projectDispatchItemRecord, projectDispatchRecord } from "./projectDispatchRecord";
import { buildQueryChips, clearAllPatch, narrowingCount } from "./queryChips";
import { defaultSortDirection, sortDispatchRows } from "./sortDispatchRows";
import { keepEqualRows } from "./stableRows";
import { type ValueOption, valueOptions } from "./valueOptions";

/** The rows grouped under one field's values, with the group headers the table draws between them. */
export type GroupedDispatchRows = { groups: DispatchGroup[]; rows: DispatchTableRow[] };

const NO_PREVIOUS: ReadonlyMap<string, DispatchTableRow> = new Map();

const NO_COLUMNS: ReadonlySet<string> = new Set();

/**
 * The filter a search term compiles to: the term over the view's search fields alone, with every control and ad-hoc
 * condition left out, so a search finds any driver the member can see whatever the filters hide. The filters stay in
 * the view's state untouched, so emptying the box brings them straight back.
 *
 * @param query - The resolved query, whose `search` names the fields the term scans.
 * @param search - The term in the search box; blank compiles to match-all (`null`).
 */
export function compileSearchFilter(
  query: Pick<ResolvedDispatchQuery, "controlValues" | "search">,
  search: string,
): FilterNode | null {
  return compileDispatchFilter(
    { controls: [], controlValues: query.controlValues, search: query.search, adhoc: [] },
    search,
  );
}

/**
 * The rows a filter keeps, plus the driver whose popover is open on a driver-per-row view, so acknowledging from
 * History never pulls the row out from under the panel.
 */
function keepMatching(
  rows: DispatchTableRow[],
  filterNode: FilterNode | null,
  openMembershipId: string | undefined,
  now: number | null,
): DispatchTableRow[] {
  if (!filterNode) return rows;
  return rows.filter(
    (row) =>
      (row.kind === "driver" && row.driver.workspaceMembershipId === openMembershipId) ||
      matchesFilter(filterNode, row.record, now ?? undefined),
  );
}

const rowId = (row: DispatchTableRow) => row.id;

/**
 * Projects the store's drivers (or their messages, at the message grain) to table rows, each joined to its driver and
 * flattened once so filters, sort, and cells agree.
 *
 * A row in `previous` built from the same driver object (and item, at the message grain) and the same waiting count is
 * returned as is rather than projected again. The caller passes `previous` only while `grain` and `tagIdBySlug` hold.
 */
export function projectRows(
  grain: ResolvedDispatchView["grain"],
  rows: readonly DriverRow[],
  items: readonly DispatchItem[],
  tagIdBySlug: ReadonlyMap<string, string>,
  unacknowledgedByFeed: ReadonlyMap<string, number>,
  previous: ReadonlyMap<string, DispatchTableRow> = NO_PREVIOUS,
): DispatchTableRow[] {
  const waitingCount = (driver: DriverRow) =>
    driver.feedId != null ? (unacknowledgedByFeed.get(driver.feedId) ?? 0) : 0;
  if (grain === "message") {
    // Each driver projects once; every message on their channel spreads that base under its own message fields.
    const driversById = new Map(rows.map((driver) => [driver.workspaceMembershipId, driver]));
    const baseByDriver = new Map<string, ProjectedRecord>();
    return items.flatMap((item): DispatchTableRow[] => {
      const driver = driversById.get(item.driverMembershipId);
      if (!driver) return [];
      // A message row reads its acknowledgement from the item, so its driver and item are all it depends on.
      const prior = previous.get(item.id);
      if (prior?.kind === "message" && prior.item === item && prior.driver === driver) return [prior];
      let base = baseByDriver.get(item.driverMembershipId);
      if (!base) {
        base = projectDispatchRecord(driver, tagIdBySlug, waitingCount(driver) > 0);
        baseByDriver.set(item.driverMembershipId, base);
      }
      return [{ kind: "message", id: item.id, item, driver, record: projectDispatchItemRecord(base, driver, item) }];
    });
  }
  return rows.map((driver) => {
    const unacknowledgedCount = waitingCount(driver);
    const prior = previous.get(driver.workspaceMembershipId);
    if (prior?.kind === "driver" && prior.driver === driver && prior.unacknowledgedCount === unacknowledgedCount) {
      return prior;
    }
    return {
      kind: "driver",
      id: driver.workspaceMembershipId,
      driver,
      unacknowledgedCount,
      record: projectDispatchRecord(driver, tagIdBySlug, unacknowledgedCount > 0),
    };
  });
}

/**
 * The rows the resolved view yields from the store, and every way the query over them changes: project, then sort,
 * then filter, then group.
 *
 * Owns the transient search term (kept with the tab, cleared when the view changes), which sets every filter aside
 * while it holds text: the table shows the term's matches across all rows, and the filters come back unchanged once it
 * empties. Also owns the strip's chips and their clears, the control, ad-hoc, sort, and grouping writers, all of them patches on the view's
 * state, and how the strip and the menus name a field. Time predicates re-evaluate on the minute clock so rows age in
 * and out on a quiet desk. On a driver-per-row view the driver whose popover is open stays in the table once the
 * filters no longer match them: opening History may acknowledge their channel, and an "Unacknowledged only" view must
 * not pull the row out from under the panel. On a message-per-row view nothing is exempt, or opening History would
 * grow the table by every message of that driver. A change of sort or grouping that changes anything is reported to
 * Amplitude from here, where the value before and after are both known.
 */
export function useDispatchQuery({
  workspaceId,
  definition,
  resolved,
  rows,
  items,
  tagsBySlug,
  tagIdBySlug,
  openMembershipId,
  now,
  unacknowledgedByFeed,
  write,
  assignmentFold,
  me,
}: {
  /** The workspace the page is on, stamped on the events it reports. */
  workspaceId: string;
  definition: DispatchViewDefinition;
  resolved: ResolvedDispatchView;
  rows: readonly DriverRow[];
  items: readonly DispatchItem[];
  tagsBySlug: ReadonlyMap<string, TagDefinition>;
  tagIdBySlug: ReadonlyMap<string, string>;
  /** The driver whose popover is open, exempt from the filters on driver rows. */
  openMembershipId: string | undefined;
  /** The page's minute clock, read only by filters with a time predicate. */
  now: number;
  /** Which channels still wait on a dispatcher, from the acknowledgement stack. */
  unacknowledgedByFeed: ReadonlyMap<string, number>;
  /** Where every query change goes, as a patch on the view's state. */
  write: (patch: DispatchViewStatePatch) => void;
  /** The reassignment prototype's open assignments; empty while the prototype is off, which pins no shelf. */
  assignmentFold: AssignmentFold;
  /** The viewer's membership id, whom the shelf's "Assigned to you" names. */
  me: string;
}): {
  projected: DispatchTableRow[];
  /** The rows the view's filters keep, search aside: the working set the tab badge and title flash watch. */
  filteredByView: DispatchTableRow[];
  /** The rows the table shows: the search's matches while it has a term, else {@link filteredByView}. */
  filtered: DispatchTableRow[];
  grouped: GroupedDispatchRows | null;
  tableRows: DispatchTableRow[];
  total: number;
  controlValues: DispatchControlValues;
  setControl: (id: string, value: DispatchControlValue) => void;
  chips: QueryChip[];
  /** How many chips hide rows: the Filter button's count, and whether the page reads as filtered. */
  filterCount: number;
  /** Whether the search box holds a term, which sets every filter aside until it empties. */
  searching: boolean;
  clearSearch: () => void;
  clearAll: () => void;
  addCondition: (condition: FilterNode) => void;
  replaceConditions: (field: DispatchField, next: FilterNode[]) => void;
  /** The values the ad-hoc conditions on a field name, for an editor to open on. */
  chosenValuesFor: (field: DispatchField) => string[];
  /** Replaces a field's ad-hoc conditions with the values chosen in an editor, or drops them for none. */
  setFieldValues: (field: DispatchField, values: string[]) => void;
  filteredColumnIds: ReadonlySet<string>;
  valueOptionsFor: (control: DispatchControl) => ValueOption[];
  valueOptionsForField: (field: DispatchField) => ValueOption[];
  naming: ConditionNaming;
  search: string;
  setSearch: (search: string) => void;
  setGroupBy: (columnId: string | null) => void;
  toggleGroup: (key: string) => void;
  sortColumn: (columnId: string, direction: "asc" | "desc") => void;
  chooseSort: (next: DisplaySort) => void;
} {
  const { grain, query } = resolved;
  const { sort, adhoc, groupBy, collapsedGroups, controlValues } = query;

  const projected = useProjectedRows(grain, rows, items, tagIdBySlug, unacknowledgedByFeed);

  const sorted = useMemo(
    () => (sort ? sortDispatchRows(projected, sort, fieldKindFor(sort.field, tagsBySlug)) : projected),
    [projected, sort, tagsBySlug],
  );

  const [search, setSearch] = useState("");
  const viewId = resolved.id;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on the view's identity, not on every object change
  useEffect(() => {
    setSearch("");
  }, [viewId]);

  // The view's filters alone, without the search: what the badge counts, and what returns once the box empties.
  const filterNode = useMemo(() => compileDispatchFilter(query, ""), [query]);
  // A filter without a time predicate leaves the clock out of its dependencies.
  const filterClock = filterNode && hasTimeOperator(filterNode) ? now : null;
  const filteredByView = useMemo(
    () => keepMatching(sorted, filterNode, openMembershipId, filterClock),
    [sorted, filterNode, openMembershipId, filterClock],
  );

  // A search sets the filters aside: it matches across every row, and the table shows its matches alone.
  const searching = search.trim() !== "";
  const searchNode = useMemo(() => compileSearchFilter(query, search), [query, search]);
  const filtered = useMemo(
    () => (searching ? keepMatching(sorted, searchNode, openMembershipId, null) : filteredByView),
    [searching, sorted, searchNode, openMembershipId, filteredByView],
  );
  const clearSearch = useCallback(() => setSearch(""), []);

  // How the strip and the menus name a field and its values: the view's column label when it has one.
  const naming = useMemo<ConditionNaming>(
    () => ({
      labelFor: (field) => labelForColumnId(field, definition, tagsBySlug),
      kindFor: (field) => fieldKindFor(field, tagsBySlug),
    }),
    [definition, tagsBySlug],
  );

  const viewGrouped = useMemo<GroupedDispatchRows | null>(() => {
    if (!groupBy) return null;
    const kind = fieldKindFor(groupBy, tagsBySlug);
    return groupDispatchRows(filtered, groupBy, kind, naming.labelFor(groupBy), new Set(collapsedGroups));
  }, [groupBy, filtered, tagsBySlug, naming, collapsedGroups]);

  // The reassignment prototype's shelf and team queue, pinned above the view's own rows whatever the filters and the
  // search say; the view's grouping as it was when nothing is assigned here.
  const grouped = useMemo<GroupedDispatchRows | null>(() => {
    const shelf = withAssignmentShelf({
      sorted,
      shown: viewGrouped ?? { groups: null, rows: filtered },
      fold: assignmentFold,
      me,
      view: { id: definition.id, name: definition.label },
    });
    return shelf ? foldShelfGroups(shelf, new Set(collapsedGroups)) : viewGrouped;
  }, [sorted, viewGrouped, filtered, assignmentFold, me, definition.id, definition.label, collapsedGroups]);

  const setControl = useCallback(
    (id: string, value: DispatchControlValue) => write({ query: { controls: { [id]: value } } }),
    [write],
  );
  // The view's own sort, what a cleared sort returns to; a view that says none has none to return to.
  const viewSort = definition.query.sort === NONE ? undefined : definition.query.sort;
  const chips = useMemo(
    () => buildQueryChips(query, resolved.overrides, naming, write, viewSort),
    [query, resolved.overrides, naming, write, viewSort],
  );
  const filterCount = useMemo(() => narrowingCount(chips), [chips]);
  const clearAll = useCallback(() => {
    setSearch("");
    write(clearAllPatch(query, viewSort));
  }, [write, query, viewSort]);
  const addCondition = useCallback(
    (condition: FilterNode) => write({ query: { adhoc: [...adhoc, condition] } }),
    [write, adhoc],
  );
  // A column's editor owns that column's conditions: applying replaces them, clearing drops them.
  const replaceConditions = useCallback(
    (field: DispatchField, next: FilterNode[]) =>
      write({ query: { adhoc: replaceFieldConditions(adhoc, field, next) } }),
    [write, adhoc],
  );
  const chosenValuesFor = useCallback((field: DispatchField) => chosenValues(adhoc, field), [adhoc]);
  const setFieldValues = useCallback(
    (field: DispatchField, values: string[]) => replaceConditions(field, valuesCondition(field, values)),
    [replaceConditions],
  );
  // The columns whose headers carry the filter mark: one per field an ad-hoc condition is about, and none while a
  // search sets the conditions aside.
  const filteredColumnIds = useMemo(() => {
    if (searching) return NO_COLUMNS;
    const ids = new Set<string>();
    for (const condition of adhoc) {
      const field = conditionField(condition);
      if (field) ids.add(columnIdForConditionField(field));
    }
    return ids;
  }, [adhoc, searching]);
  const valueOptionsForField = useCallback(
    (field: DispatchField) => valueOptions(projected, field, fieldKindFor(field, tagsBySlug)),
    [projected, tagsBySlug],
  );
  const valueOptionsFor = useCallback(
    (control: DispatchControl) => (control.kind === "values" ? valueOptionsForField(control.field) : []),
    [valueOptionsForField],
  );

  // A column id is the field it shows, so the Display menu's choices write straight into the state.
  const setGroupBy = useCallback(
    (columnId: string | null) => {
      if (columnId !== groupBy) {
        ampli.dispatchGroupByChanged({
          workspaceId,
          fromGroupBy: groupBy ?? undefined,
          toGroupBy: columnId ?? undefined,
        });
      }
      write({ query: { groupBy: columnId ?? NONE } });
    },
    [write, workspaceId, groupBy],
  );
  const toggleGroup = useCallback(
    (key: string) => write({ query: { collapsedGroups: toggleCollapsedGroup(collapsedGroups, key) } }),
    [write, collapsedGroups],
  );

  // Every sort change lands here, from a header's menu or the Display popover, so one place reports it.
  const applySort = useCallback(
    (next: DispatchSort | null) => {
      if (next?.field !== sort?.field || next?.direction !== sort?.direction) {
        ampli.dispatchSortByChanged({
          workspaceId,
          fromSortBy: sort?.field,
          fromSortDirection: sort?.direction,
          toSortBy: next?.field,
          toSortDirection: next?.direction,
        });
      }
      write({ query: { sort: next ?? NONE } });
    },
    [write, workspaceId, sort],
  );
  const sortColumn = useCallback(
    (columnId: string, direction: "asc" | "desc") => applySort({ field: columnId, direction }),
    [applySort],
  );
  // A freshly chosen field starts in the direction its kind suggests; a repeat of the current field keeps the direction
  // the popover shows.
  const chooseSort = useCallback(
    (next: DisplaySort) => {
      if (!next) {
        applySort(null);
        return;
      }
      const sameField = sort != null && sort.field === next.key;
      const direction = sameField ? next.direction : defaultSortDirection(fieldKindFor(next.key, tagsBySlug));
      applySort({ field: next.key, direction });
    },
    [applySort, tagsBySlug, sort],
  );

  return {
    projected,
    filteredByView,
    filtered,
    grouped,
    tableRows: grouped ? grouped.rows : filtered,
    total: projected.length,
    controlValues,
    setControl,
    chips,
    filterCount,
    clearAll,
    addCondition,
    replaceConditions,
    chosenValuesFor,
    setFieldValues,
    filteredColumnIds,
    valueOptionsFor,
    valueOptionsForField,
    naming,
    search,
    setSearch,
    searching,
    clearSearch,
    setGroupBy,
    toggleGroup,
    sortColumn,
    chooseSort,
  };
}

/**
 * {@link projectRows} across renders, reusing last pass's rows: an unchanged driver keeps its row object, and a pass
 * that changed no row returns last pass's array, so the sort, filter and table memos downstream all skip.
 */
function useProjectedRows(
  grain: ResolvedDispatchView["grain"],
  rows: readonly DriverRow[],
  items: readonly DispatchItem[],
  tagIdBySlug: ReadonlyMap<string, string>,
  unacknowledgedByFeed: ReadonlyMap<string, number>,
): DispatchTableRow[] {
  const previousRef = useRef({
    grain,
    tagIdBySlug,
    rows: [] as DispatchTableRow[],
    byKey: NO_PREVIOUS,
  });
  return useMemo(() => {
    const last = previousRef.current;
    // A new grain or tag set changes every record, so nothing from the last pass is reusable.
    const reusable = last.grain === grain && last.tagIdBySlug === tagIdBySlug;
    const previous = reusable ? last.byKey : NO_PREVIOUS;
    const next = projectRows(grain, rows, items, tagIdBySlug, unacknowledgedByFeed, previous);
    const kept = keepEqualRows(reusable ? last.rows : [], previous, next, rowId);
    previousRef.current = { grain, tagIdBySlug, ...kept };
    return kept.rows;
  }, [grain, rows, items, tagIdBySlug, unacknowledgedByFeed]);
}
